/**
 * Global queue of contract-SIMULATION errors (gas-free `eth_call` previews from
 * lib/previewWrite.ts) so they surface in a single modal instead of only in the
 * inline `error` state of whichever panel fired the write.
 *
 * Deliberately a module-level singleton store (not React context) because the
 * errors originate deep inside the write helpers / wagmi hooks, and the modal
 * itself is mounted once in the root route.
 *
 * `useSyncExternalStore` friendly: getSnapshot returns a stable array reference
 * that only changes when the queue changes.
 */

export type SimulationError = {
  id: number
  /** Short heading, e.g. "Simulation failed — transaction not sent". */
  title: string
  /** Decoded revert name / message shown in the modal body. */
  message: string
  /** Optional contract the call targeted (manifest key). */
  contract?: string
}

let queue: SimulationError[] = []
const listeners = new Set<() => void>()
let nextId = 1

function emit() {
  for (const l of listeners) l()
}

/** Show a simulation error. Called from previewWrite's revert path. */
export function pushSimulationError(e: Omit<SimulationError, "id">): void {
  queue = [...queue, { ...e, id: nextId++ }]
  emit()
}

/** Dismiss the oldest (top-of-stack) simulation error. */
export function dismissSimulationError(id?: number): void {
  queue = id === undefined ? queue.slice(1) : queue.filter((x) => x.id !== id)
  emit()
}

/** Drop every queued simulation error. */
export function clearSimulationErrors(): void {
  if (queue.length === 0) return
  queue = []
  emit()
}

export function getSimulationErrors(): SimulationError[] {
  return queue
}

export function subscribeSimulationErrors(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}
